import { intl, logger, ui } from '@beelday/common'
import { useSelector } from 'app-redux'
import { selectStreamReopenings } from 'event-source-manager/redux'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { UUID } from '../../common/types'
import {
	KnowledgeCheck,
	KnowledgeCheckOptionID,
	KnowledgeCheckQuestionID,
} from './knowledge-check'
import useKnowledgeChecks from './use-knowledge-checks'

/**
 * A first read that fails is tried again after these waits before the
 * participant sees an error: a phone back from another app can take a moment
 * to reach the network.
 */
export const READ_RETRY_DELAYS_MS = [1_000, 2_000, 4_000]

const calculateAnsweredQuestions = (
	answers: Record<KnowledgeCheckQuestionID, KnowledgeCheckOptionID[]>
): number => {
	return Object.keys(answers).filter(
		item => Array.isArray(answers[item]) && answers[item].length
	).length
}

export type TraineeKnowledgeCheck = {
	knowledgeCheck?: KnowledgeCheck
	error?: Error
	/**
	 * A step (previous, next, finish) is on its way to the server. The server
	 * moves one question per call, so a second tap would skip a question -
	 * callers disable their controls while this is set.
	 */
	busy: boolean
	reload: () => void
	onAnswer: (
		questionId: KnowledgeCheckQuestionID,
		answers: KnowledgeCheckOptionID[]
	) => void
	onPrev: () => void
	onNext: () => void
	onFinish: (
		questionId: KnowledgeCheckQuestionID,
		answers: KnowledgeCheckOptionID[]
	) => void
}

/**
 * A participant working through a knowledge check: the check as the server
 * sees it, and the four things they can do to it. The server owns which
 * question is current, so every step re-fetches rather than guessing.
 *
 * Ordering is enforced here rather than left to the network:
 * - answers go out one at a time, in the order they were tapped, and the
 *   check is re-read only once none are pending - otherwise a late response
 *   could untick an option tapped after it;
 * - every read is numbered and only the newest one may land, so a slow read
 *   for an earlier state cannot overwrite a newer one;
 * - steps wait for pending answers and are refused while one is in flight.
 *
 * Shared by the desktop view and the in-person phone view.
 */
export const useTraineeKnowledgeCheck = (
	knowledgeCheckId: UUID
): TraineeKnowledgeCheck => {
	const roomAddress = useAssertedJoinedRoomAddress()
	const {
		getKnowledgeCheck,
		answerQuestion,
		prevQuestion,
		nextQuestion,
		finishKnowledgeCheck,
	} = useKnowledgeChecks()
	const confirm = ui.useConfirm()

	const [knowledgeCheck, setKnowledgeCheck] = useState<KnowledgeCheck>()
	const [error, setError] = useState<Error>()
	const [busy, setBusy] = useState(false)

	const mounted = useRef(true)
	const retry = useRef<ReturnType<typeof setTimeout>>()
	const latestRead = useRef(0)
	const answers = useRef<Promise<unknown>>(Promise.resolve())
	const pendingAnswers = useRef(0)
	const stepping = useRef(false)

	useEffect(
		() => () => {
			mounted.current = false
			if (retry.current) clearTimeout(retry.current)
		},
		[]
	)

	/** Lands only if no newer read - and no tap - came after it. */
	const read = useCallback((): Promise<KnowledgeCheck> => {
		const request = ++latestRead.current
		return getKnowledgeCheck(roomAddress, knowledgeCheckId).then(fetched => {
			if (mounted.current && request === latestRead.current) {
				setKnowledgeCheck(fetched)
			}
			return fetched
		})
	}, [getKnowledgeCheck, roomAddress, knowledgeCheckId])

	const reload = useCallback(() => {
		setError(undefined)
		if (retry.current) clearTimeout(retry.current)
		const attempt = (failures: number): void => {
			read().catch(e => {
				if (!mounted.current) return
				const wait = READ_RETRY_DELAYS_MS[failures]
				if (wait !== undefined) {
					retry.current = setTimeout(() => attempt(failures + 1), wait)
					return
				}
				logger.error('Failed to fetch knowledge check: ' + knowledgeCheckId, e)
				setError(e)
			})
		}
		attempt(0)
	}, [read, knowledgeCheckId])

	useEffect(reload, [reload])

	// A check that never loaded is read again once a lost stream is back: the
	// network is back with it. A loaded one is not - a read landing between
	// taps could untick an answer still on its way.
	const reopenings = useSelector(selectStreamReopenings)
	const seenReopenings = useRef(reopenings)
	useEffect(() => {
		if (reopenings === seenReopenings.current) return
		seenReopenings.current = reopenings
		if (!knowledgeCheck) reload()
	}, [reopenings, knowledgeCheck, reload])

	/** Runs after every answer tapped so far has been sent. */
	const afterAnswers = <T,>(task: () => Promise<T>): Promise<T> => {
		const next = answers.current.then(task)
		answers.current = next.catch(() => undefined)
		return next
	}

	const sendAnswer = (
		questionId: KnowledgeCheckQuestionID,
		selected: KnowledgeCheckOptionID[]
	): Promise<unknown> => {
		pendingAnswers.current += 1
		return afterAnswers(() =>
			answerQuestion(roomAddress, knowledgeCheckId, questionId, selected)
		).finally(() => {
			pendingAnswers.current -= 1
		})
	}

	/** Shows `selected` for one question, keeping everything else. */
	const showAnswer = (
		questionId: KnowledgeCheckQuestionID,
		selected: KnowledgeCheckOptionID[],
		/** Only while the question still shows this selection. */
		ifShowing?: KnowledgeCheckOptionID[]
	) =>
		setKnowledgeCheck(current =>
			current &&
			(ifShowing === undefined ||
				current.state.answers[questionId] === ifShowing)
				? {
						...current,
						state: {
							...current.state,
							answers: { ...current.state.answers, [questionId]: selected },
						},
				  }
				: current
		)

	const onAnswer = (
		questionId: KnowledgeCheckQuestionID,
		selected: KnowledgeCheckOptionID[]
	) => {
		const previous = knowledgeCheck?.state.answers[questionId] ?? []
		// A read already on its way predates this tap: let it not land, or it
		// would untick what was just ticked - and the next tap would build on
		// the stale selection and send it.
		latestRead.current += 1
		showAnswer(questionId, selected)
		sendAnswer(questionId, selected)
			.catch(e => {
				logger.error('Failed to answer a knowledge check', e)
				// Not saved, so not shown: the re-read below would correct it,
				// but it fails too when the network is gone. A later tap on the
				// same question is left alone - it has its own send.
				showAnswer(questionId, previous, selected)
			})
			.then(() => (pendingAnswers.current === 0 ? read() : undefined))
			.catch(e => logger.error('Failed to fetch knowledge check', e))
	}

	/** One step at a time; a second tap while one is in flight is dropped. */
	const step = (action: () => Promise<unknown>) => {
		if (stepping.current) {
			return
		}
		stepping.current = true
		setBusy(true)
		afterAnswers(action)
			.then(read)
			.catch(e => logger.error('Knowledge check step failed', e))
			.finally(() => {
				stepping.current = false
				if (mounted.current) {
					setBusy(false)
				}
			})
	}

	const onPrev = () => step(() => prevQuestion(roomAddress, knowledgeCheckId))

	const onNext = () => step(() => nextQuestion(roomAddress, knowledgeCheckId))

	const askToFinish = (fetched: KnowledgeCheck): Promise<unknown> =>
		confirm
			.generic(
				<intl.Translate>
					knowledge_check.question.finish.popup.title
				</intl.Translate>,
				fetched.questions.length ===
					calculateAnsweredQuestions(fetched.state.answers) ? (
					<intl.Translate>
						knowledge_check.question.finish.popup.desc.confirm
					</intl.Translate>
				) : (
					<intl.Translate>
						knowledge_check.question.finish.popup.desc.no_answers
					</intl.Translate>
				),
				<intl.Translate>
					knowledge_check.question.finish.popup.confirm
				</intl.Translate>,
				<intl.Translate>
					knowledge_check.question.finish.popup.cancel
				</intl.Translate>
			)
			.then(
				// The step re-reads once this settles; reading here as well
				// fetched the finished check twice.
				() => finishKnowledgeCheck(roomAddress, knowledgeCheckId),
				// Cancelling the dialog rejects; staying on the question is the
				// whole point of cancelling, so it is not an error.
				() => undefined
			)

	const onFinish = (
		questionId: KnowledgeCheckQuestionID,
		selected: KnowledgeCheckOptionID[]
	) =>
		step(() =>
			answerQuestion(roomAddress, knowledgeCheckId, questionId, selected)
				.then(() => read())
				.then(askToFinish)
		)

	return {
		knowledgeCheck,
		error,
		busy,
		reload,
		onAnswer,
		onPrev,
		onNext,
		onFinish,
	}
}

export default useTraineeKnowledgeCheck
